import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { CheckCircleIcon as CheckCircleSolid } from "@heroicons/react/24/solid";
import { MinusCircleIcon } from "@heroicons/react/24/outline";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/Button";
import toast from "react-hot-toast";
import { reportsApi } from "../api/reports";
import { termsApi } from "../api/terms";
import { activitiesApi } from "../api/activities";
import { completionsApi } from "../api/completions";
import type { AcBookActivity, Term, Activity } from "../types";
import { sortAcs } from "../utils/acSort";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CellState {
  checked: boolean;
  date: string; // YYYY-MM-DD or ""
  notes: string;
}

// Key: "camperId:acId"
type EntryState = Record<string, CellState>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function cellKey(camperId: number, acId: number): string {
  return `${camperId}:${acId}`;
}

function initEntryState(data: AcBookActivity[]): EntryState {
  const state: EntryState = {};
  for (const entry of data) {
    const gradAcId = entry.activity.graduationAcId;
    for (const cabin of entry.cabins) {
      for (const camper of cabin.campers) {
        for (const ac of entry.activity.achievementCredits) {
          const completion = camper.completions[ac.id];
          state[cellKey(camper.id, ac.id)] = {
            checked: completion?.status === "completed",
            date: completion?.status === "completed" ? (completion.completedDate ?? "") : "",
            notes: completion?.notes ?? "",
          };
        }
        if (gradAcId != null) {
          const gradCompletion = camper.completions[gradAcId];
          state[cellKey(camper.id, gradAcId)] = {
            checked: gradCompletion?.status === "completed",
            date:
              gradCompletion?.status === "completed" ? (gradCompletion.completedDate ?? "") : "",
            notes: gradCompletion?.notes ?? "",
          };
        }
      }
    }
  }
  return state;
}

const UNSAVED_MSG = "You have unsaved changes. Leave without saving?";

// ─── AC checkbox cell ─────────────────────────────────────────────────────────

function AcCell({
  camperId,
  acId,
  entryDate,
  cell,
  onChange,
  highlightReady = false,
}: {
  camperId: number;
  acId: number;
  entryDate: string;
  cell: CellState;
  onChange: (key: string, next: CellState) => void;
  highlightReady?: boolean;
}) {
  const key = cellKey(camperId, acId);

  function toggle() {
    if (cell.checked) {
      onChange(key, { checked: false, date: "", notes: cell.notes });
    } else {
      onChange(key, { checked: true, date: entryDate || todayIso(), notes: cell.notes });
    }
  }

  const ringClass = highlightReady ? "ring-2 ring-amber-400 ring-offset-1" : "";
  const titleText = highlightReady
    ? "All ACs complete — ready to graduate"
    : cell.checked
      ? "Click to clear"
      : "Click to mark completed";

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={toggle}
        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${ringClass} ${
          cell.checked
            ? "bg-forest-500 border-forest-500 text-white hover:bg-forest-600 hover:border-forest-600"
            : "bg-white border-stone-300 text-stone-300 hover:border-forest-400"
        }`}
        title={titleText}
      >
        {cell.checked ? (
          <CheckCircleSolid className="w-5 h-5" />
        ) : (
          <MinusCircleIcon className="w-5 h-5" />
        )}
      </button>
      {cell.checked && cell.date && cell.date !== entryDate && (
        <span className="text-[10px] text-stone-400">{cell.date}</span>
      )}
    </div>
  );
}

// ─── Cabin section ────────────────────────────────────────────────────────────

function CabinSection({
  cabin,
  acs,
  graduationAcId,
  entryDate,
  state,
  onChange,
  rowOffset,
}: {
  cabin: AcBookActivity["cabins"][0];
  acs: { id: number; name: string; code: string; displayNumber: number }[];
  graduationAcId: number | null;
  entryDate: string;
  state: EntryState;
  onChange: (key: string, next: CellState) => void;
  rowOffset: number;
}) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden mb-6">
      {/* Cabin header */}
      <div className="px-5 py-3 bg-stone-50 border-b border-stone-200">
        <span className="text-base font-semibold text-stone-800">{cabin.name}</span>
      </div>

      {/* Camper rows */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-stone-100">
              <th className="sticky left-0 z-10 bg-stone-50 px-5 py-2 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide w-48 shadow-[1px_0_0_0_rgb(231_229_228)]">
                Camper
              </th>
              {acs.map((ac) => (
                <th
                  key={ac.id}
                  className="px-3 py-2 text-center text-xs font-semibold text-stone-500 uppercase tracking-wide min-w-[120px]"
                  title={ac.name}
                >
                  <div>{ac.displayNumber}</div>
                  <div className="font-normal normal-case text-stone-400 text-[10px] truncate max-w-[110px]">
                    {ac.name}
                  </div>
                </th>
              ))}
              {graduationAcId != null && (
                <th
                  className="px-3 py-2 text-center text-xs font-semibold text-amber-700 uppercase tracking-wide min-w-[80px] border-l border-stone-200"
                  title="Graduation"
                >
                  Grad
                </th>
              )}
              <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide min-w-[160px]">
                Notes
              </th>
            </tr>
          </thead>
          <tbody>
            {cabin.campers.map((camper, idx) => {
              const isOdd = (rowOffset + idx) % 2 === 1;
              const rowBg = isOdd ? "bg-stone-50" : "bg-white";
              const allStandardChecked =
                acs.length > 0 &&
                acs.every((a) => state[cellKey(camper.id, a.id)]?.checked);
              const gradCell =
                graduationAcId != null
                  ? state[cellKey(camper.id, graduationAcId)]
                  : null;
              const showReadyHint =
                graduationAcId != null && allStandardChecked && !gradCell?.checked;
              return (
                <tr key={camper.id} className={`${rowBg} hover:bg-forest-50/40`}>
                  <td
                    className={`sticky left-0 z-10 ${rowBg} px-5 py-3 text-sm font-medium text-stone-800 whitespace-nowrap shadow-[1px_0_0_0_rgb(231_229_228)]`}
                  >
                    {camper.lastName}, {camper.firstName}
                  </td>
                  {acs.map((ac) => {
                    const key = cellKey(camper.id, ac.id);
                    const cell = state[key] ?? { checked: false, date: "", notes: "" };
                    return (
                      <td key={ac.id} className="px-3 py-3 text-center align-top">
                        <AcCell
                          camperId={camper.id}
                          acId={ac.id}
                          entryDate={entryDate}
                          cell={cell}
                          onChange={onChange}
                        />
                      </td>
                    );
                  })}
                  {graduationAcId != null && (
                    <td className="px-3 py-3 text-center align-top border-l border-stone-200">
                      <AcCell
                        camperId={camper.id}
                        acId={graduationAcId}
                        entryDate={entryDate}
                        cell={
                          gradCell ?? { checked: false, date: "", notes: "" }
                        }
                        onChange={onChange}
                        highlightReady={showReadyHint}
                      />
                    </td>
                  )}
                  {/* Notes — one notes field per camper, shared across all ACs for this activity */}
                  <td className="px-3 py-3 align-top">
                    <input
                      type="text"
                      placeholder="Notes…"
                      value={
                        // Use notes from first AC cell as the per-camper note for this activity
                        acs.length > 0 ? (state[cellKey(camper.id, acs[0].id)]?.notes ?? "") : ""
                      }
                      onChange={(e) => {
                        // Sync notes to all ACs for this camper in this activity
                        for (const ac of acs) {
                          const k = cellKey(camper.id, ac.id);
                          const cell = state[k] ?? { checked: false, date: "", notes: "" };
                          onChange(k, { ...cell, notes: e.target.value });
                        }
                      }}
                      className="w-full text-sm rounded border border-stone-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-forest-500"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AcBookEntry() {
  const [terms, setTerms] = useState<Term[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [termId, setTermId] = useState<number | null>(null);
  const [activityId, setActivityId] = useState<number | null>(null);
  const [entryDate, setEntryDate] = useState<string>(todayIso());
  const [data, setData] = useState<AcBookActivity[]>([]);
  const [entryState, setEntryState] = useState<EntryState>({});
  const [originalState, setOriginalState] = useState<EntryState>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load terms + activities on mount
  useEffect(() => {
    termsApi
      .list()
      .then((raw) => {
        const sorted = [...raw].sort(
          (a, b) => b.year - a.year || (a.startDate ?? "").localeCompare(b.startDate ?? "")
        );
        setTerms(sorted);
      })
      .catch(() => toast.error("Failed to load terms"));

    activitiesApi
      .list()
      .then((raw) => {
        const sorted = [...raw].sort((a, b) => a.name.localeCompare(b.name));
        setActivities(sorted);
      })
      .catch(() => toast.error("Failed to load activities"));
  }, []);

  // Fetch data when term or activity changes
  useEffect(() => {
    if (!termId || !activityId) return;
    setLoading(true);
    reportsApi
      .acBook(termId, activityId)
      .then((result) => {
        setData(result);
        const init = initEntryState(result);
        setEntryState(init);
        setOriginalState(init);
      })
      .catch(() => toast.error("Failed to load AC book data"))
      .finally(() => setLoading(false));
  }, [termId, activityId]);

  const handleCellChange = useCallback((key: string, next: CellState) => {
    setEntryState((prev) => ({ ...prev, [key]: next }));
  }, []);

  const dirtyCount = Object.entries(entryState).filter(([key, cell]) => {
    const orig = originalState[key];
    return cell.checked !== orig?.checked || cell.date !== orig?.date || cell.notes !== orig?.notes;
  }).length;
  const isDirty = dirtyCount > 0;

  // Keep latest dirty flag in a ref for event handlers
  const isDirtyRef = useRef(isDirty);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Warn on browser navigation (close tab, refresh, external link) when dirty
  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      // Modern browsers ignore the custom string but still show their generic prompt.
      e.returnValue = UNSAVED_MSG;
      return UNSAVED_MSG;
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Warn on in-app navigation via <a> / <Link> clicks while dirty
  useEffect(() => {
    function handleDocClick(e: MouseEvent) {
      if (!isDirtyRef.current) return;
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!anchor) return;
      // Ignore non-navigating clicks
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (anchor.target && anchor.target !== "_self") return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      // Same-page link?
      if (anchor.href === window.location.href) return;
      const ok = window.confirm(UNSAVED_MSG);
      if (!ok) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
    document.addEventListener("click", handleDocClick, true);
    return () => document.removeEventListener("click", handleDocClick, true);
  }, []);

  function confirmIfDirty(): boolean {
    if (!isDirty) return true;
    return window.confirm(UNSAVED_MSG);
  }

  function handleTermChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value === "" ? null : Number(e.target.value);
    if (next === termId) return;
    if (!confirmIfDirty()) return;
    setTermId(next);
  }

  function handleActivityChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value === "" ? null : Number(e.target.value);
    if (next === activityId) return;
    if (!confirmIfDirty()) return;
    setActivityId(next);
  }

  async function handleSave() {
    if (data.length === 0) return;

    // Collect all completions to save (only checked ones or ones where checked state changed)
    const completions: { camperId: number; achievementCreditId: number; status: string; completedDate?: string; notes?: string }[] = [];

    for (const [key, cell] of Object.entries(entryState)) {
      const orig = originalState[key];
      const changed =
        cell.checked !== orig?.checked ||
        cell.date !== orig?.date ||
        cell.notes !== orig?.notes;

      if (!changed) continue;

      const [camperIdStr, acIdStr] = key.split(":");
      const camperId = Number(camperIdStr);
      const achievementCreditId = Number(acIdStr);

      if (cell.checked) {
        completions.push({
          camperId,
          achievementCreditId,
          status: "completed",
          completedDate: cell.date || undefined,
          notes: cell.notes || undefined,
        });
      } else if (orig?.checked) {
        // Was checked, now unchecked → mark as pending to clear it
        completions.push({
          camperId,
          achievementCreditId,
          status: "pending",
          completedDate: undefined,
          notes: cell.notes || undefined,
        });
      }
    }

    if (completions.length === 0) {
      toast("No changes to save.");
      return;
    }

    setSaving(true);
    try {
      await completionsApi.bulkCreate(completions);
      setOriginalState({ ...entryState });
      toast.success(`Saved ${completions.length} record${completions.length !== 1 ? "s" : ""}`);
    } catch {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  }

  const selectedTerm = terms.find((t) => t.id === termId);
  const termLabel = selectedTerm ? `${selectedTerm.year} — ${selectedTerm.name}` : "";
  const selectedActivity = activities.find((a) => a.id === activityId);

  return (
    <div>
      <PageHeader
        title="AC Book Entry"
        subtitle={
          selectedActivity || termLabel
            ? [termLabel, selectedActivity?.name].filter(Boolean).join(" · ")
            : undefined
        }
        actions={
          <Button
            variant="primary"
            loading={saving}
            onClick={handleSave}
            disabled={dirtyCount === 0 || loading}
          >
            Save All Changes{dirtyCount > 0 ? ` (${dirtyCount})` : ""}
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* Sticky controls — Term, Activity, Date stay visible while scrolling */}
        <div className="sticky top-0 z-20 -mx-6 lg:-mx-8 px-6 lg:px-8 py-3 bg-white/95 backdrop-blur border-b border-stone-200 shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            {/* Term selector */}
            <div className="flex items-center gap-2">
              <label htmlFor="term-select" className="text-sm font-medium text-stone-600">
                Term
              </label>
              {terms.length === 0 ? (
                <span className="text-sm text-stone-400">
                  No terms —{" "}
                  <Link to="/admin/terms" className="text-forest-700 hover:underline">
                    add terms
                  </Link>
                </span>
              ) : (
                <select
                  id="term-select"
                  value={termId ?? ""}
                  onChange={handleTermChange}
                  className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
                >
                  <option value="">— Select term —</option>
                  {terms.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.year} — {t.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Activity selector */}
            <div className="flex items-center gap-2">
              <label htmlFor="activity-select" className="text-sm font-medium text-stone-600">
                Activity
              </label>
              <select
                id="activity-select"
                value={activityId ?? ""}
                onChange={handleActivityChange}
                className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
              >
                <option value="">— Select activity —</option>
                {activities.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Single date for the entire AC book entry session */}
            <div className="flex items-center gap-2">
              <label htmlFor="entry-date" className="text-sm font-medium text-stone-600 whitespace-nowrap">
                Date
              </label>
              <input
                id="entry-date"
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </div>

            {isDirty && (
              <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Unsaved changes ({dirtyCount})
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !activityId || data.length === 0 ? (
          <div className="text-center py-16 text-stone-400 text-sm">
            {!activityId
              ? "Select an activity to begin entry."
              : "No campers enrolled for this term, or no ACs defined for this activity."}
          </div>
        ) : (
          (() => {
            let rowOffset = 0;
            const sections: React.ReactNode[] = [];
            for (const entry of data) {
              for (const cabin of entry.cabins) {
                sections.push(
                  <CabinSection
                    key={`${entry.activity.id}-${cabin.id}`}
                    cabin={cabin}
                    acs={sortAcs(entry.activity.achievementCredits)}
                    graduationAcId={entry.activity.graduationAcId}
                    entryDate={entryDate}
                    state={entryState}
                    onChange={handleCellChange}
                    rowOffset={rowOffset}
                  />
                );
                rowOffset += cabin.campers.length;
              }
            }
            return sections;
          })()
        )}

        {/* Bottom save button */}
        {!loading && data.length > 0 && (
          <div className="flex justify-end pt-2">
            <Button
              variant="primary"
              loading={saving}
              onClick={handleSave}
              disabled={dirtyCount === 0}
            >
              Save All Changes{dirtyCount > 0 ? ` (${dirtyCount})` : ""}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
